import React, { FC } from 'react';
import { QueryClient, QueryClientProvider, QueryFilters, QueryKey } from '@tanstack/react-query';
import { IS_LOCAL_DEVELOPMENT } from '../../../const/env';
import { isUndefined } from '../../../predicates/type';
import { normalizeReactQueryKey } from './utils';

const hasReactQueryDevtools = window.localStorage.getItem('deezze_reactQueryDevtools') === 'true';

const ReactQueryDevtools = React.lazy(() =>
	import('@tanstack/react-query-devtools/build/modern/production.js').then((d) => ({
		default: d.ReactQueryDevtools,
	})),
);

const _queryClientV5 = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			retry: 3,
			retryDelay: 4000,
			staleTime: 1500,
			refetchInterval: false,
			refetchOnReconnect: false,
			throwOnError: false,
		},
		mutations: {
			throwOnError: true,
		},
	},
});

const queryClientV5 = {
	getQueryData: (queryKey: QueryKey) => {
		const normalizedQueryKey = normalizeReactQueryKey(queryKey);

		return _queryClientV5.getQueryData(normalizedQueryKey);
	},
	getQueryState: (queryKey: QueryKey) => {
		const normalizedQueryKey = normalizeReactQueryKey(queryKey);

		return _queryClientV5.getQueryState(normalizedQueryKey);
	},
	removeQueries: (filters?: QueryFilters) => {
		const normalizedFilter = filters || {};

		if (!isUndefined(normalizedFilter.queryKey)) {
			normalizedFilter.queryKey = normalizeReactQueryKey(normalizedFilter.queryKey);
		}

		return _queryClientV5.removeQueries(normalizedFilter);
	},
	refetchQueries: (filters?: QueryFilters) => {
		const normalizedFilter = filters || {};

		if (!isUndefined(normalizedFilter.queryKey)) {
			normalizedFilter.queryKey = normalizeReactQueryKey(normalizedFilter.queryKey);
		}

		return _queryClientV5.refetchQueries(normalizedFilter);
	},
	setQueryData: (queryKey: QueryKey, updater) => {
		const normalizedQueryKey = normalizeReactQueryKey(queryKey);

		return _queryClientV5.setQueryData(normalizedQueryKey, updater);
	},
};

const ReactQueryConfigProviderV5: FC<React.PropsWithChildren> = ({ children }) => {
	const [showDevtools, setShowDevtools] = React.useState(IS_LOCAL_DEVELOPMENT || hasReactQueryDevtools);

	React.useEffect(() => {
		// @ts-expect-error
		window.toggleDevtools = () => setShowDevtools((old) => !old);
	}, []);

	return (
		<QueryClientProvider client={_queryClientV5}>
			{showDevtools && (
				<React.Suspense fallback={null}>
					<ReactQueryDevtools
						position='bottom'
						buttonPosition='bottom-left'
					/>
				</React.Suspense>
			)}
			{children}
		</QueryClientProvider>
	);
};

export { ReactQueryConfigProviderV5, queryClientV5 };
